<template>
  <StandardCard
    @action-clicked="handleActionClicked"
    :actions="cardActions"
    :show-default-slot="tracksPublicRecord"
    :title="title"
    card-test-id="public-record-section"
    border
    tile
  >
    <v-alert
      v-if="hasPreviousLicenseRecords"
      class="mb-4"
      color="warning"
      data-testid="public-record-license-mismatch-banner"
      type="warning"
      variant="tonal"
    >
      {{ $bct('Some public records were logged under a previous provider license.') }}
    </v-alert>

    <div
      v-if="publicRecords.length > 0"
      class="d-flex flex-column ga-4"
    >
      <div
        v-for="record in publicRecords"
        :key="record.id"
        class="d-flex flex-column ga-4"
        data-testid="public-record-item"
      >
        <div class="d-flex align-center justify-space-between ga-4">
          <div
            class="d-flex flex-wrap align-center ga-2"
            data-testid="public-record-metadata"
          >
            <v-tooltip
              v-if="publicRecordPublishedTooltip(record)"
              location="bottom"
            >
              <template #activator="{ props: tooltipProps }">
                <StandardChip
                  v-bind="tooltipProps"
                  :active="isPublicRecordPublished(record)"
                  role="publishedChip"
                  size="small"
                />
              </template>
              <span>{{ publicRecordPublishedTooltip(record) }}</span>
            </v-tooltip>
            <StandardChip
              v-else
              :active="isPublicRecordPublished(record)"
              role="publishedChip"
              size="small"
            />
            <StandardChip
              :text="publicRecordSchemaName(record)"
              role="plain"
              size="small"
            />
            <StandardChip
              :text="record.source_event_type ?? 'No event type'"
              role="plain"
              size="small"
            />
            <StandardChip
              :text="`${record.severity ?? 'No'} severity`"
              role="warning"
              size="small"
            />
          </div>

          <StandardButton
            @click="handlePublicRecordEdit(record)"
            prepend-icon="edit"
            role="link"
            text="Edit"
          />
        </div>

        <p
          class="fs-16 text-wrap text-break ws-pre-line mb-0 min-w-0"
          data-testid="public-record-summary"
        >
          {{ record.summary ?? 'No summary' }}
        </p>

        <div class="d-flex flex-wrap align-center justify-space-between ga-4">
          <div class="d-flex align-center ga-2 min-w-0">
            <v-icon
              class="material-icons-outlined flex-shrink-0"
              color="black"
              icon="calendar_today"
            />
            <LongDate
              :date="record.event_date"
              date-classes="fs-16"
              prefix="Event: "
            />
          </div>

          <div class="d-flex align-center ga-2 min-w-0">
            <v-icon
              v-if="record.resolved_date"
              class="material-icons-outlined flex-shrink-0"
              color="success"
              icon="check_circle"
            />
            <v-icon
              v-else
              class="material-icons-outlined flex-shrink-0"
              color="error"
              icon="cancel"
            />
            <LongDate
              :date="record.resolved_date"
              date-classes="fs-16"
              nulltext="Not resolved"
              prefix="Resolved: "
            />
          </div>
        </div>

        <v-divider v-if="record.id !== publicRecords[publicRecords.length - 1]?.id" />
      </div>
    </div>

    <div
      v-else-if="publicRecordLoading"
      class="d-flex justify-center py-4"
    >
      <v-progress-linear indeterminate />
    </div>

    <div
      v-else-if="publicRecordLoadFailed"
      class="d-flex flex-column align-center ga-2 py-4"
      data-testid="public-record-load-error"
    >
      <p class="mb-0 fs-16 text-center">
        {{ $bct('Could not load public record.') }}
      </p>
      <StandardButton
        @click="loadPublicRecord"
        :processing="publicRecordLoading"
        role="link"
        text="Try again"
      />
    </div>
  </StandardCard>

  <ResourceDialog
    @save="handleSchemaSelected"
    ref="schemaSelectDialogRef"
    :fields="schemaSelectFields"
    title="Create public record"
    close-on-save
  />

  <SchematizedQuestionStepper
    v-if="effectiveSchemaId"
    @save="handleSaved"
    ref="publicRecordStepperRef"
    :on-save="savePublicRecord"
    :schema-id="effectiveSchemaId"
    synced-model="PublicRecord"
  />
</template>

<script setup>
import { useStore } from 'vuex';

import LongDate from '@/shared/components/LongDate.vue';
import ResourceDialog from '@/shared/components/form/ResourceDialog.vue';
import SchematizedQuestionStepper from '@/shared/components/question/SchematizedQuestionStepper.vue';
import useApi from '@/shared/composables/useApi';
import useDateFormatting from '@/shared/composables/useDateFormatting';

const props = defineProps({
  licenseId: { type: String, default: null },
  publicRecordId: { type: String, default: null },
  schemaId: { type: String, default: null },
  sourceEventId: { type: String, default: null },
  sourceEventType: { type: String, default: null },
  title: { type: String, default: 'Public record' },
  workflowInstanceId: { type: String, default: null },
});

const emit = defineEmits(['save']);

const $bct = inject('translator');
const { api } = useApi();
const { formatDate } = useDateFormatting();
const store = useStore();

const publicRecordStepperRef = ref(null);
const schemaSelectDialogRef = ref(null);
const publicRecords = ref([]);
const publicRecordLoading = ref(false);
const publicRecordLoadFailed = ref(false);
const selectedSchemaId = ref(null);
const activeStepperSchemaId = ref(null);

const publicRecordSchemas = computed(() =>
  Object.values(store.state.schemas || {}).filter((schema) => schema.data_type === 'PublicRecord'),
);

const usesAssessmentSource = computed(
  () => props.sourceEventType === 'Assessment' && Boolean(props.sourceEventId),
);

const tracksPublicRecord = computed(
  () =>
    Boolean(props.publicRecordId) ||
    (Boolean(props.sourceEventType) && Boolean(props.sourceEventId)),
);

const usesSourceEventLookup = computed(
  () => Boolean(props.sourceEventType) && Boolean(props.sourceEventId) && !props.publicRecordId,
);

const showCreateButton = computed(() => {
  if (publicRecordLoading.value || publicRecordLoadFailed.value) return false;
  if (usesSourceEventLookup.value) return true;

  return publicRecords.value.length === 0;
});

const cardActions = computed(() => {
  const shouldShowCreate = !tracksPublicRecord.value || showCreateButton.value;
  if (!shouldShowCreate) return [];

  return [
    {
      event: 'create',
      fullWidth: true,
      role: 'create',
      title: 'Create public record',
      variant: 'outlined',
    },
  ];
});

const hasPreviousLicenseRecords = computed(() => {
  if (!props.licenseId) return false;

  return publicRecords.value.some(
    (record) => record.license_id && record.license_id !== props.licenseId,
  );
});

const effectiveSchemaId = computed(
  () =>
    props.schemaId ??
    selectedSchemaId.value ??
    activeStepperSchemaId.value ??
    (publicRecordSchemas.value.length === 1 ? publicRecordSchemas.value[0].id : null),
);

const schemaSelectFields = computed(() => [
  {
    required: true,
    text: 'Schema',
    value: 'schema_id',
    items: publicRecordSchemas.value,
    itemText: 'name',
    itemValue: 'id',
  },
]);

watch(
  () => [props.publicRecordId, props.sourceEventType, props.sourceEventId, props.licenseId],
  loadPublicRecord,
  { immediate: true },
);

function handleActionClicked({ action }) {
  if (action === 'create') {
    handlePublicRecordCreate();
  }
}

async function loadPublicRecord() {
  activeStepperSchemaId.value = null;

  if (props.publicRecordId) {
    await loadPublicRecordById();
    return;
  }

  if (props.sourceEventType && props.sourceEventId) {
    await loadPublicRecordBySource();
    return;
  }

  publicRecords.value = [];
  publicRecordLoading.value = false;
  publicRecordLoadFailed.value = false;
}

async function loadPublicRecordById() {
  publicRecordLoading.value = true;
  publicRecordLoadFailed.value = false;

  const response = await api.shared.publicRecord.get(props.publicRecordId);

  publicRecordLoading.value = false;

  if (response?.status === 200 && response?.data?.id) {
    publicRecords.value = [response.data];
    return;
  }

  publicRecords.value = [];
  publicRecordLoadFailed.value = true;
}

async function loadPublicRecordBySource() {
  publicRecordLoading.value = true;
  publicRecordLoadFailed.value = false;

  try {
    if (!usesAssessmentSource.value) {
      publicRecords.value = [];
      publicRecordLoadFailed.value = true;
      return;
    }

    const response = await api.shared.assessmentPublicRecord.index(props.sourceEventId);

    if (response?.status === 200 && Array.isArray(response.data)) {
      publicRecords.value = response.data;
      publicRecordLoadFailed.value = false;
      return;
    }

    publicRecords.value = [];
    publicRecordLoadFailed.value = true;
  } catch {
    publicRecords.value = [];
    publicRecordLoadFailed.value = true;
  } finally {
    publicRecordLoading.value = false;
  }
}

async function savePublicRecord(record) {
  if (record.id) {
    return api.shared.publicRecord.update(record.id, record);
  }

  if (usesAssessmentSource.value) {
    return api.shared.assessmentPublicRecord.create(props.sourceEventId, record);
  }

  const payload = {
    ...record,
    license_id: props.licenseId,
  };

  if (props.workflowInstanceId) {
    payload.workflow_instance_id = props.workflowInstanceId;
  }

  return api.shared.publicRecord.create(payload);
}

function handlePublicRecordCreate() {
  const createSchemaId = resolveCreateSchemaId();
  if (!createSchemaId) {
    schemaSelectDialogRef.value?.open();
    return;
  }

  publicRecordStepperRef.value?.open({ schema_id: createSchemaId, custom: {} });
}

function resolveCreateSchemaId() {
  if (props.schemaId) return props.schemaId;

  if (publicRecordSchemas.value.length === 1) return publicRecordSchemas.value[0].id;

  return null;
}

async function handleSchemaSelected({ schema_id: schemaId }) {
  selectedSchemaId.value = schemaId;
  await nextTick();
  publicRecordStepperRef.value?.open({ schema_id: schemaId, custom: {} });
}

async function handlePublicRecordEdit(record) {
  activeStepperSchemaId.value = record.schema_id;
  await nextTick();
  publicRecordStepperRef.value?.open(record);
}

function isPublicRecordPublished(record) {
  return Boolean(record.published_date);
}

function publicRecordPublishedTooltip(record) {
  if (!record.published_date) return null;

  return `${$bct('Published')}: ${formatDate(record.published_date)}`;
}

function publicRecordSchemaName(record) {
  return store.state.schemas?.[record.schema_id]?.name ?? 'Unknown schema';
}

function handleSaved(savedRecord) {
  const existingIndex = publicRecords.value.findIndex((record) => record.id === savedRecord.id);
  if (existingIndex >= 0) {
    publicRecords.value[existingIndex] = savedRecord;
  } else {
    publicRecords.value = [...publicRecords.value, savedRecord];
  }

  emit('save', savedRecord);
}
</script>
